import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import { routes } from 'src/client/utils/router';
import { FooterBarModal } from 'src/client/containers/FooterBar/FooterBarModal';
import { FooterCategoryMenu } from './FooterCategoryMenu';
import { OnPageFooter } from 'src/client/components/OnPageSeo/OnPageFooter';
import { FooterBarNewsletter } from './FooterBarNewsletter';
import { FooterCompliance } from './FooterCompliance';
import { ConstrainedContainer, DESKTOP, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { FooterBarSocials } from './FooterBarSocials';
import { getFooterCategoryLabels } from './footerCategoryLables';

const FooterSection = styled(ConstrainedContainer)`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 298px;
  padding: 0;

  @media ${TABLET_PORTRAIT} {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 15px 0 15px;
    gap: 20px;
    min-height: auto;
  }

  @media ${DESKTOP} {
    flex-wrap: nowrap;
  }
`;

const FooterMenu = styled.ul`
  display: grid;
  grid-auto-rows: min-content;
  grid-template-columns: 1fr;
  margin: 0 auto;
  list-style: none;
  width: 100%;
  padding: 0;

  @media ${TABLET_PORTRAIT} {
    grid-column-gap: 30px;
    grid-template-columns: repeat(4, 1fr);
  }

  @media ${DESKTOP} {
    width: auto;
  }
`;

const FooterBackgroundContainer = styled.div`
  background: ${(props) => props.theme.footerBarPrimary1};
  color: ${(props) => props.theme.footerBarPrimary2};

  @media ${TABLET_PORTRAIT} {
    padding-top: 20px;
  }
`;

export const FooterBar = () => {
  const user = useSelector((state) => state.auth.user);
  const siteAbbreviation = useSelector((state) => state.site.abbreviation);
  const { ssoUrl, config } = useSelector((state) => state.app);

  const [openedCategories, setOpenCategories] = useState<string[]>([]);
  const [infoModal, setInfoModal] = useState<boolean>(false);

  const { hasAccountCenter } = config;
  const {
    camsUrl,
    customerSupportUrl,
    hasActorsWanted,
    hasCommunityGuidelines,
    hasDirectoresWanted,
    hasLatestUpdates,
    hasPhilosophy,
    hasPhotographersWanted,
    hasReportAbuselink,
    privacyUrl,
    termsUrl,
    webmastersUrl,
  } = config.footer;

  const labels = getFooterCategoryLabels(siteAbbreviation);

  return (
    <>
      <OnPageFooter />
      <FooterBarModal onHide={() => setInfoModal(!infoModal)} privacyUrl={privacyUrl} show={infoModal} />
      <FooterBackgroundContainer>
        <FooterSection>
          <FooterBarNewsletter setInfoModal={setInfoModal} infoModal={infoModal} />

          <FooterMenu>
            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="amazing"
              label="Amazing Content"
              links={[
                ...(hasLatestUpdates
                  ? [
                      {
                        to: '/updates',
                        label: 'Latest Updates',
                      },
                    ]
                  : []),
                {
                  to: '/erotic-films',
                  label: labels['/erotic-films'],
                },
                {
                  to: '/galleries',
                  label: labels['/galleries'],
                },
                {
                  to: '/models',
                  label: labels['/models'],
                },
                ...(config.galleries.botd.active
                  ? [
                      {
                        to: '/photo-archive',
                        label: 'Beauty of the Day',
                      },
                    ]
                  : []),
                {
                  to: camsUrl,
                  openNewWindow: true,
                  label: 'Live Cams',
                },
                {
                  to: `https://goddess.me/?utm_medium=web&utm_source=${siteAbbreviation}&utm_campaign=footer`,
                  openNewWindow: true,
                  label: 'Intimate Toys',
                },
                ...(config.footer.aboutUsUrl
                  ? [
                      {
                        to: config.footer.aboutUsUrl,
                        label: 'About Us',
                      },
                    ]
                  : []),
              ]}
            />

            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="affiliate"
              label="Become Affiliated"
              links={[
                {
                  to: webmastersUrl,
                  isExternal: true,
                  label: 'Webmasters',
                },
                ...(hasActorsWanted
                  ? [
                      {
                        to: 'https://help.metart.network/artists-models/become-a-model/model-submission-guidelines',
                        isExternal: true,
                        label: 'Models Apply Here',
                        className: 'standout',
                      },
                    ]
                  : []),
                ...(hasPhotographersWanted
                  ? [
                      {
                        to:
                          'https://help.metart.network/artists-models/become-a-photographer/photographer-technical-guidelines',
                        isExternal: true,
                        label: 'Photographers Wanted',
                      },
                    ]
                  : []),
                ...(hasDirectoresWanted
                  ? [
                      {
                        to:
                          'https://help.metart.network/artists-models/become-a-director/director-technical-guidelines',
                        isExternal: true,
                        label: 'Directors Wanted',
                      },
                    ]
                  : []),
                ...(hasPhilosophy
                  ? [
                      {
                        to: 'https://help.metart.network/welcome/company-philosophy',
                        isExternal: true,
                        label: 'Philosophy',
                      },
                    ]
                  : []),
                ...(hasCommunityGuidelines
                  ? [
                      {
                        to: ' https://help.metart.network/policies-rules/community-guidelines',
                        isExternal: true,
                        label: 'Community Guidelines',
                      },
                    ]
                  : []),
                {
                  to: routes.report.routes.abuse.path,
                  label: 'Report Abuse',
                },
              ]}
            />

            <FooterCategoryMenu
              openedCategories={openedCategories}
              setOpenCategories={setOpenCategories}
              id="service"
              label="Account & Services"
              links={[
                ...(!user
                  ? [
                      {
                        to: ssoUrl,
                        isExternal: true,
                        label: 'Member Login',
                      },
                      {
                        to: `${ssoUrl}/forgot-password`,
                        isExternal: true,
                        label: 'Lost Password',
                      },
                    ]
                  : []),
                ...(user && hasAccountCenter
                  ? [
                      {
                        to: ssoUrl,
                        isExternal: true,
                        openNewWindow: true,
                        label: 'Manage Account',
                      },
                    ]
                  : []),
                {
                  to: customerSupportUrl,
                  isExternal: true,
                  label: 'Customer Support',
                },
                {
                  to: termsUrl,
                  isExternal: true,
                  label: 'Terms & Conditions',
                },
                {
                  to: privacyUrl,
                  isExternal: true,
                  label: 'Privacy',
                },
                ...(hasReportAbuselink
                  ? [
                      {
                        to: '/piracy',
                        label: 'Report Piracy',
                      },
                    ]
                  : []),
              ]}
            />

            <FooterBarSocials />
          </FooterMenu>
        </FooterSection>
        <FooterCompliance />
      </FooterBackgroundContainer>
    </>
  );
};
